import * as yup from 'yup';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import _values from 'lodash/values';
import { BANNER_CTA_TYPE } from 'commons/models/banner.model';
import { APP_LANGUAGE } from 'commons/models/common';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	EMPTY_ARRAY,
	CASE,
	BOOLEAN_OPTIONS,
	AssetUploadInputOnS3,
	ASSET_TYPE
} from 'commons-ui';

import { ZUPEE_GAMES } from 'pages/dashboard/tournamentV2/utils/constants';
import { ScreenName } from 'commons/models/referral';
import { CTA_TYPE_OPTIONS, CTA_VALUE_OPTIONS, CTA_VALUE_OPTIONS_MAP, LANG_OPTIONS } from './constants';

const setValueUndefined = (props: any) => {
	const { name, setValue } = props;
	setValue(name, undefined);
};

export const ctaValueDependencyResolver = (
	props: any,
	dependencyValues: string[]
): {
	disabled: boolean;
	InputComponent?: typeof SelectInput | typeof TextInput;
} | null => {
	const ctaType = _get(dependencyValues, '0');
	const { InputComponent } = props;
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		setValueUndefined(props);
		return { disabled: true };
	}
	if (ctaType === BANNER_CTA_TYPE.IN_APP && InputComponent !== SelectInput) {
		return { InputComponent: SelectInput, disabled: false };
	}
	if (InputComponent !== TextInput) {
		return { InputComponent: TextInput, disabled: false };
	}

	return null;
};

export const ctaParamsGameNameDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled: boolean;
	hidden?: boolean;
	InputComponent?: typeof SelectInput;
} => {
	const ctaType = _get(dependencyValues, '0');
	const ctaValue = _get(dependencyValues, '1');
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		return { disabled: true };
	}

	if (ctaType === BANNER_CTA_TYPE.IN_APP) {
		if (ctaValue === CTA_VALUE_OPTIONS.find((item) => item.id === CTA_VALUE_OPTIONS_MAP.GENERIC_WEBVIEW)?.id) {
			return { hidden: true, disabled: true };
		}
		return { InputComponent: SelectInput, disabled: false };
	}

	return { disabled: true };
};

export const ctaParamsUrlDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled?: boolean;
	hidden?: boolean;
	InputComponent?: typeof TextInput;
} | null => {
	const ctaValue = _get(dependencyValues, '1');

	if (ctaValue === CTA_VALUE_OPTIONS.find((item) => item.id === CTA_VALUE_OPTIONS_MAP.GENERIC_WEBVIEW)?.id) {
		return { InputComponent: TextInput, disabled: false };
	}

	return { hidden: true };
};

export const FORM_FIELD_KEYS = {
	CODE: 'code',
	TYPE: 'type',
	CTA: 'cta',
	CTA_TYPE: 'cta_type',
	CTA_VALUE: 'cta_value',
	CTA_SELECT_VALUE: 'cta_select_value',
	CTA_PARAMS: 'cta_params',
	CTA_PARAMS_GAMENAME: 'cta_params_gamename',
	CTA_PARAMS_URL: 'cta_params_url',
	IMAGE_RATIO: 'imageRatio',
	LANGUAGE: 'language',
	PLACEMENT_INDEX: 'placementIndex',
	IMAGE_URL: 'imageUrl',
	IS_ACTIVE: 'isActive',
	MAX_APP_VERSION_SUPPORTED: 'maxAppVersion',
	MIN_APP_VERSION_SUPPORTED: 'minAppVersion'
};

export const validationSchema = yup.object().shape({
	code: yup.string().required('Code is a required field'),
	type: yup.string().required('Banner Type is a required field'),
	cta: yup
		.object()
		.shape({
			type: yup
				.string()
				.oneOf(_keys(BANNER_CTA_TYPE), 'CTA Type should be one of IN_APP, DEEP_LINK, EXTERNAL_LINK, WEBVIEW_LINK'),
			value: yup.string().when('cta.type', {
				is: (value: string[]) => value != null,
				then: yup.string().oneOf(_keys(ScreenName)).required()
			}),
			params: yup.object().shape({
				gameName: yup.string().uppercase(),
				url: yup.string().test('url-validation', 'Invalid URL', (value) => {
					if (value === '') return true;
					if (!value) return true;
					return yup.string().url('Invalid URL').isValidSync(value);
				})
			})
		})
		.optional(),
	imageUrl: yup.string().required('Banner can not be created without the image is a required field'),
	placementIndex: yup.string().required('PlacementIndex is a required field'),
	language: yup
		.string()
		.oneOf(_values(APP_LANGUAGE), 'Language should be one of HINDI or ENGLISH')
		.required('Language is a required field'),
	minAppVersion: yup.number().required('min app version is required'),
	maxAppVersion: yup.number().required('max app version is required')
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.CODE]: new RenderConfig()
		.setName('code')
		.setLabel('Code')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, letterCase: CASE.UPPERCASE, required: true }),
	[FORM_FIELD_KEYS.TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			letterCase: CASE.UPPERCASE,
			required: true
		}),
	[FORM_FIELD_KEYS.CTA]: new RenderConfig().setName('cta').setLabel('CTA').setRenderProps({ required: false }),
	[FORM_FIELD_KEYS.CTA_TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: CTA_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.CTA_VALUE]: new RenderConfig()
		.setName('value')
		.setLabel('Value')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: CTA_VALUE_OPTIONS,
			dependencies: ['cta.type'],
			dependencyResolver: ctaValueDependencyResolver
		}),
	[FORM_FIELD_KEYS.CTA_PARAMS]: new RenderConfig().setName('params').setLabel('Params').setRenderProps({ required: false }),
	[FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('gameName')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: ZUPEE_GAMES,
			dependencies: ['cta.type', 'cta.value'],
			dependencyResolver: ctaParamsGameNameDependencyResolver
		}),
	[FORM_FIELD_KEYS.CTA_PARAMS_URL]: new RenderConfig()
		.setName('url')
		.setLabel('URL')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			dependencies: ['cta.type', 'cta.value'],
			dependencyResolver: ctaParamsUrlDependencyResolver
		}),
	[FORM_FIELD_KEYS.LANGUAGE]: new RenderConfig()
		.setName('language')
		.setLabel('Language')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: LANG_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.PLACEMENT_INDEX]: new RenderConfig()
		.setName('placementIndex')
		.setLabel('Placement Index')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			required: true
		}),
	[FORM_FIELD_KEYS.IMAGE_URL]: new RenderConfig()
		.setName('imageUrl')
		.setLabel('Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true, assetType: ASSET_TYPE.IMAGE }),
	[FORM_FIELD_KEYS.MIN_APP_VERSION_SUPPORTED]: new RenderConfig()
		.setName('minAppVersion')
		.setLabel('Min App Version Supported')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.MAX_APP_VERSION_SUPPORTED]: new RenderConfig()
		.setName('maxAppVersion')
		.setLabel('Max App Version Supported')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.IS_ACTIVE]: new RenderConfig()
		.setName('isActive')
		.setLabel('Banner Deployed?')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CODE)]), new Column([new Row(FORM_FIELD_KEYS.TYPE)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.LANGUAGE)]),
		new Column([new Row(FORM_FIELD_KEYS.PLACEMENT_INDEX)])
	]),
	new Row(
		FORM_FIELD_KEYS.CTA,
		EMPTY_ARRAY,
		[new Column([new Row(FORM_FIELD_KEYS.CTA_TYPE)]), new Column([new Row(FORM_FIELD_KEYS.CTA_VALUE)])],
		[
			new Row(FORM_FIELD_KEYS.CTA_PARAMS, EMPTY_ARRAY, [
				new Column([new Row(FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME), new Row(FORM_FIELD_KEYS.CTA_PARAMS_URL)])
			])
		]
	),
	new Row(FORM_FIELD_KEYS.IMAGE_URL),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.MIN_APP_VERSION_SUPPORTED)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_APP_VERSION_SUPPORTED)])
	])
];

export const getBannerFormDefaults = (): {
	placementIndex: string;
	isActive: boolean;
} => {
	return {
		placementIndex: '0',
		isActive: false
	};
};
